import React from "react";
import { Box, Typography, FormControl, Select, MenuItem, Button } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

const ProductInfo = ({
  product,
  isMobile,
  selectedVariant,
  selectedVariantId,
  setSelectedVariantId,
  handleAddToCart,
  handleViewDetails,
}) => {
  const computePrices = () => {
    if (!selectedVariant) {
      const originalNet = Number(product?.pvp1 ?? 0);
      const finalNet = product?.precio_descuento != null ? Number(product.precio_descuento) : originalNet;

      const ivaOriginal = Number(product?.iva_valor ?? 0);
      const ivaFinal =
        product?.precio_descuento != null
          ? Number(product.iva_aplicado_valor ?? product.iva_valor ?? 0)
          : ivaOriginal;

      return {
        price_with_iva_original: originalNet + ivaOriginal,
        price_with_iva_final: finalNet + ivaFinal,
      };
    }

    const originalNet = Number(selectedVariant.price ?? 0);
    const finalNet = selectedVariant.precio_descuento != null ? Number(selectedVariant.precio_descuento) : originalNet;

    const ivaOriginal = Number(selectedVariant.iva_valor ?? 0);
    const ivaFinal =
      selectedVariant.precio_descuento != null
        ? Number(selectedVariant.iva_aplicado_valor ?? selectedVariant.iva_valor ?? 0)
        : ivaOriginal;

    return {
      price_with_iva_original: originalNet + ivaOriginal,
      price_with_iva_final: finalNet + ivaFinal,
    };
  };

  const prices = computePrices();

  const priceOriginalWithIva = prices.price_with_iva_original.toFixed(2);
  const priceFinalWithIva = prices.price_with_iva_final.toFixed(2);

  const hasDiscount =
    Number(prices.price_with_iva_final) < Number(prices.price_with_iva_original) - 1e-6;

  const refLine = selectedVariant?.sku ? `REF: ${product.code} - SKU: ${selectedVariant.sku}` : `REF: ${product.code}`;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        gap: { xs: 0.25, sm: 0.55 },
        pt: { xs: 0.1, sm: 0.2 },
      }}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Typography
        variant="subtitle1"
        onClick={(e) => {
          e.stopPropagation();
          if (typeof handleViewDetails === "function") handleViewDetails();
        }}
        sx={{
          mt: 0,
          fontWeight: 400,
          fontSize: { xs: "0.92rem", sm: "1.05rem" },
          color: "#000",
          textAlign: "left",
          lineHeight: 1.2,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 3,
          minHeight: { xs: "3.2em", sm: "3.6em" },
          cursor: "pointer",
          "&:hover": {
            color: "#1565C0",
            textDecoration: "underline",
          },
        }}
      >
        {product.name}
      </Typography>

      <Box sx={{ mt: 0 }}>
        <Typography
          sx={{
            color: "#555",
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
            textAlign: "left",
            lineHeight: 1.1,
          }}
        >
          {product.brand}
        </Typography>
        <Typography
          sx={{
            color: "#777",
            fontSize: { xs: "0.78rem", sm: "0.86rem" },
            textAlign: "left",
            lineHeight: 1.1,
          }}
        >
          Precio incluye IVA
        </Typography>
      </Box>

      <Box sx={{ display: "none" }}>
        <Typography sx={{ fontSize: "0.75rem" }}>{refLine}</Typography>
      </Box>

      {product?.variants?.length ? (
        <Box sx={{ mt: { xs: 0.35, sm: 0.65 } }}>
          <Typography
            sx={{
              color: "#666",
              fontSize: { xs: "0.78rem", sm: "0.86rem" },
              mb: 0.35,
              textAlign: "left",
              lineHeight: 1.1,
            }}
          >
            {product.variants?.[0]?.variant_type}
          </Typography>

          <FormControl size="small" fullWidth>
            <Select
              value={selectedVariantId}
              onChange={(e) => setSelectedVariantId(e.target.value)}
              displayEmpty
              sx={{
                height: { xs: 34, sm: 38 },
                fontSize: { xs: "0.86rem", sm: "0.9rem" },
              }}
              renderValue={(sel) =>
                !sel ? (
                  <Typography sx={{ color: "#999", fontSize: { xs: "0.86rem", sm: "0.9rem" } }}>
                    Seleccionar
                  </Typography>
                ) : (
                  product.variants.find((v) => String(v.id) === String(sel))?.value
                )
              }
            >
              <MenuItem value="" disabled>
                Seleccionar
              </MenuItem>
              {product.variants.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedVariant ? (
            <Box sx={{ mt: { xs: 0.4, sm: 0.75 } }}>
              <Box
                sx={{
                  minHeight: { xs: 30, sm: 40 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {hasDiscount && (
                  <Typography
                    sx={{
                      color: "#9e9e9e",
                      textDecoration: "line-through",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      textAlign: "center",
                    }}
                  >
                    $ {priceOriginalWithIva}
                  </Typography>
                )}

                <Typography
                  sx={{
                    color: "#1565C0",
                    fontWeight: 600,
                    fontSize: { xs: "1.02rem", sm: "1.18rem" },
                    textAlign: "center",
                  }}
                >
                  $ {priceFinalWithIva}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mt: { xs: 0.4, sm: 0.75 },
                minHeight: { xs: 30, sm: 40 },
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Typography
                sx={{
                  color: "#999",
                  fontWeight: 600,
                  textAlign: "left",
                  fontSize: { xs: "0.86rem", sm: "0.9rem" },
                }}
              >
                Seleccionar {product.variants?.[0]?.variant_type}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: { xs: 0.35, sm: 0.65 } }}>
          <Box
            sx={{
              minHeight: { xs: 30, sm: 40 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              width: "100%",
              textAlign: "center",
            }}
          >
            {hasDiscount && (
              <Typography
                sx={{
                  color: "#9e9e9e",
                  textDecoration: "line-through",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  textAlign: "center",
                }}
              >
                $ {priceOriginalWithIva}
              </Typography>
            )}

            <Typography
              sx={{
                color: "#1565C0",
                fontWeight: 600,
                fontSize: { xs: "1.02rem", sm: "1.18rem" },
                textAlign: "center",
              }}
            >
              $ {priceFinalWithIva}
            </Typography>
          </Box>
        </Box>
      )}

      <Button
        variant="outlined"
        color="primary"
        disabled={product?.variants?.length ? !selectedVariant : false}
        onClick={(e) => {
          e.stopPropagation();
          handleAddToCart(e, {
            price_with_iva: Number(priceFinalWithIva),
            variant_id: selectedVariantId || null,
          });
        }}
        sx={{
          textTransform: "none",
          mt: { xs: 0.55, sm: 1 },
          alignSelf: "center",
          borderColor: "#1976d2",
          color: "#1976d2",
          backgroundColor: "#fff",
          width: { xs: "82%", sm: "85%" },
          height: { xs: 32, sm: 36 },
          fontSize: { xs: "0.8rem", sm: "0.95rem" },
        }}
        startIcon={<ShoppingCartIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
      >
        Agregar
      </Button>
    </Box>
  );
};

export default ProductInfo;
